import { useCallback } from "react"
import type { GuidebookPlace } from "./types"
import { CloseIcon } from "./icons"

interface Props {
  place: GuidebookPlace | null
  dialogRef: React.RefObject<HTMLDialogElement | null>
  onClose: () => void
}

export function PlaceDetailSheet({ place, dialogRef, onClose }: Props) {
  const copy = place?.en

  const handleDialogClick = useCallback(
    (e: React.MouseEvent<HTMLDialogElement>) => {
      if (e.target === dialogRef.current) {
        onClose()
      }
    },
    [dialogRef, onClose],
  )

  return (
    <dialog ref={dialogRef} className="gb-detail-dialog" onClick={handleDialogClick} onClose={onClose}>
      <div className="gb-detail">
        <div className="gb-detail__header">
          <div className="gb-detail__handle" aria-hidden="true">
            <div className="gb-detail__handle-bar" />
          </div>
          <button
            className="gb-detail__close"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        {copy && place && (
          <div className="gb-detail__body">
            <h3 className="gb-detail__name">{copy.name}</h3>

            <div className="gb-detail__meta">
              {place.area && <span className="gb-detail__area-pill">{place.area}</span>}
              {place.type && <span className="gb-detail__type">{place.type}</span>}
              {place.priceRange && <span className="gb-detail__price">{place.priceRange}</span>}
            </div>

            {copy.short && <p className="gb-detail__description">{copy.short}</p>}

            {copy.why && (
              <div className="gb-detail__section">
                <h4 className="gb-detail__section-title">Why visit</h4>
                <p className="gb-detail__section-text">{copy.why}</p>
              </div>
            )}

            {copy.tip && (
              <div className="gb-detail__section">
                <h4 className="gb-detail__section-title">Tip</h4>
                <p className="gb-detail__section-text">{copy.tip}</p>
              </div>
            )}

            {copy.rainAlternative && (
              <div className="gb-detail__section">
                <h4 className="gb-detail__section-title">Rainy day alternative</h4>
                <p className="gb-detail__section-text">{copy.rainAlternative}</p>
              </div>
            )}

            {(place.website || (place.coordinates?.lat != null && place.coordinates?.lng != null)) && (
              <div className="gb-detail__actions">
                {place.website && (
                  <a
                    className="gb-detail__action-btn"
                    href={place.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Website
                  </a>
                )}
                {place.coordinates?.lat != null && place.coordinates?.lng != null && (
                  <a
                    className="gb-detail__action-btn gb-detail__action-btn--primary"
                    href={`https://maps.google.com/?q=${place.coordinates.lat},${place.coordinates.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Get directions
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </dialog>
  )
}
